import { Controller } from "@hotwired/stimulus"
import Plyr from "plyr"
import { audioLoadQueue } from "../lib/audio_load_queue"

const SPINNER =
  '<svg class="animate-spin h-4 w-4 text-blue-500" viewBox="0 0 24 24" fill="none">' +
  '<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>' +
  '<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>'

const ICONS = {
  queued: '<span class="text-gray-400" title="W kolejce">◷</span>',
  loading: SPINNER,
  loaded: '<span class="text-green-600 font-bold" title="Załadowano">✓</span>',
  failed: '<span class="text-red-600 font-bold" title="Nie udało się załadować">✗</span>'
}

const LOAD_TIMEOUT = 30000

// A single audio player whose download is scheduled through the global queue
// (max 2 at a time). Shows load status and offers a retry on failure.
export default class extends Controller {
  static targets = ["audio", "status", "retry"]
  static values = { src: String }

  connect() {
    this.player = new Plyr(this.audioTarget, {
      controls: ["play", "progress", "current-time", "duration"],
      invertTime: false
    })
    this.onLoaded = this.onLoaded.bind(this)
    this.onError = this.onError.bind(this)
    this.audioTarget.addEventListener("canplaythrough", this.onLoaded)
    this.audioTarget.addEventListener("error", this.onError)
    this.enqueue()
  }

  disconnect() {
    this.release()
    if (this.task) audioLoadQueue.remove(this.task)
    this.audioTarget.removeEventListener("canplaythrough", this.onLoaded)
    this.audioTarget.removeEventListener("error", this.onError)
    this.player?.destroy()
  }

  enqueue() {
    this.settled = false
    this.setStatus("queued")
    this.task = { run: () => this.load() }
    audioLoadQueue.enqueue(this.task)
  }

  // Retry button: put a failed (or still-queued) player back at the end of the line.
  retry() {
    if (this.task) audioLoadQueue.remove(this.task)
    this.enqueue()
  }

  load() {
    this.setStatus("loading")
    return new Promise((resolve) => {
      this.resolveLoad = resolve
      this.timer = setTimeout(() => this.settle("failed"), LOAD_TIMEOUT)
      this.audioTarget.src = this.srcValue
      this.audioTarget.load()
    })
  }

  onLoaded() {
    this.settle("loaded")
  }

  onError() {
    this.settle("failed")
  }

  // Free the queue slot if we get torn down mid-load.
  release() {
    if (!this.settled && this.resolveLoad) this.settle("queued")
  }

  settle(state) {
    if (this.settled) return
    this.settled = true
    clearTimeout(this.timer)
    this.setStatus(state)
    this.resolveLoad?.()
  }

  setStatus(state) {
    if (this.hasStatusTarget) this.statusTarget.innerHTML = ICONS[state] || ""
    if (this.hasRetryTarget) this.retryTarget.classList.toggle("hidden", state !== "failed")
  }
}
